import { type ReactNode, useMemo } from "react";
import type { ModelChoice } from "../../lib/types";
import { decodeModel, encodeModel } from "../../lib/modelValue";
import { cn } from "../../lib/cn";

/**
 * Provider + Model + Effort dropdowns over the shared model catalog, composing
 * the single `[<cli>:]<model>[@<effort>]` value every surface stores. Renders
 * labeled cells as a fragment so the parent can place them in its own grid. The
 * Effort cell is always rendered (disabled with a "—" when the chosen model has
 * no effort control) so layouts don't jump as models change.
 *
 * Two layouts:
 * - `crossProvider` (default): a separate Provider dropdown filters the model
 *   list — the explicit three-control layout used by the task + track forms.
 * - compact (`crossProvider={false}`): no Provider dropdown; the single Model
 *   dropdown lists **all** providers' models (Codex models get a `codex:`
 *   prefix relative to `defaultProvider`), so both providers are pickable in
 *   one place — used by the project default and the loop worker.
 *
 * `agentType` seeds the Provider dropdown for a task that already ran on a
 * non-default CLI (the runner strips the prefix and stashes the CLI there).
 */
export interface ModelPickerProps {
  value: string;
  onChange: (value: string) => void;
  models: ModelChoice[];
  crossProvider?: boolean;
  defaultProvider?: string;
  agentType?: string | null;
  disabled?: boolean;
  modelPlaceholder?: string;
  effortPlaceholder?: string;
}

const selectClasses = cn(
  "w-full h-9 px-3 bg-bg-inset rounded-md",
  "border border-transparent hover:border-border-default focus:border-border-strong",
  "text-body text-text-primary outline-none transition-colors duration-fast ease-out",
  "disabled:opacity-50 disabled:pointer-events-none",
);

export function ModelPicker({
  value,
  onChange,
  models,
  crossProvider = true,
  defaultProvider,
  agentType,
  disabled = false,
  modelPlaceholder = "use project default",
  effortPlaceholder = "default",
}: ModelPickerProps) {
  const decoded = useMemo(() => decodeModel(value), [value]);
  const combined = !crossProvider;
  const baseProvider = defaultProvider || "claude";

  // A model's option value. In compact mode it's provider-qualified relative to
  // the base provider (Codex on a Claude project → `codex:gpt-5.5`); in
  // cross-provider mode it's the bare id (the Provider dropdown carries the CLI).
  const optionValue = useMemo(
    () =>
      (m: ModelChoice): string =>
        combined && m.cli_binary !== baseProvider
          ? `${m.cli_binary}:${m.id}`
          : m.id,
    [combined, baseProvider],
  );

  // A task that already ran loses its provider prefix; seed it from agentType
  // when that CLI differs from the project's default.
  const provider = useMemo(() => {
    if (decoded.provider) return decoded.provider;
    if (crossProvider && agentType && defaultProvider && agentType !== defaultProvider) {
      return agentType;
    }
    return "";
  }, [decoded.provider, crossProvider, agentType, defaultProvider]);

  const providers = useMemo(() => {
    const set = new Set<string>();
    for (const m of models) set.add(m.cli_binary);
    return [...set].sort();
  }, [models]);

  const effectiveProvider = provider || defaultProvider || "";
  const modelOptions = useMemo(() => {
    if (combined) return models;
    return effectiveProvider
      ? models.filter((m) => m.cli_binary === effectiveProvider)
      : models;
  }, [combined, models, effectiveProvider]);

  // The model dropdown's current value, in whatever form its options use.
  const modelSelectValue = combined
    ? encodeModel({ provider: decoded.provider, model: decoded.model, effort: "" })
    : decoded.model;

  const selectedModel = useMemo(() => {
    if (combined) return models.find((m) => optionValue(m) === modelSelectValue);
    return models.find(
      (m) => m.cli_binary === effectiveProvider && m.id === decoded.model,
    );
  }, [combined, models, optionValue, modelSelectValue, effectiveProvider, decoded.model]);
  const effortOptions = selectedModel?.efforts ?? [];

  // The CLI prefix to keep when only the effort changes.
  const encodeProvider = combined ? decoded.provider : provider;

  const onProvider = (p: string) =>
    onChange(encodeModel({ provider: p, model: "", effort: "" }));

  const onModel = (selectedValue: string) => {
    if (combined) {
      const d = decodeModel(selectedValue);
      const opts =
        models.find((m) => optionValue(m) === selectedValue)?.efforts ?? [];
      onChange(
        encodeModel({
          provider: d.provider,
          model: d.model,
          effort: opts.includes(decoded.effort) ? decoded.effort : "",
        }),
      );
      return;
    }
    const opts =
      models.find(
        (m) => m.cli_binary === effectiveProvider && m.id === selectedValue,
      )?.efforts ?? [];
    onChange(
      encodeModel({
        provider,
        model: selectedValue,
        effort: opts.includes(decoded.effort) ? decoded.effort : "",
      }),
    );
  };

  const onEffort = (e: string) =>
    onChange(encodeModel({ provider: encodeProvider, model: decoded.model, effort: e }));

  return (
    <>
      {crossProvider && (
        <PickerField label="Provider">
          <select
            className={selectClasses}
            value={provider}
            onChange={(e) => onProvider(e.target.value)}
            disabled={disabled}
          >
            <option value="">use project default</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {providerLabel(p)}
              </option>
            ))}
          </select>
        </PickerField>
      )}
      <PickerField label="Model">
        <select
          className={selectClasses}
          value={modelSelectValue}
          onChange={(e) => onModel(e.target.value)}
          disabled={disabled}
        >
          <option value="">{modelPlaceholder}</option>
          {modelOptions.map((m) => (
            <option key={`${m.cli_binary}:${m.id}`} value={optionValue(m)}>
              {m.label}
              {m.default ? " (default)" : ""}
            </option>
          ))}
        </select>
      </PickerField>
      <PickerField label="Effort">
        <select
          className={selectClasses}
          value={decoded.effort}
          onChange={(e) => onEffort(e.target.value)}
          disabled={disabled || effortOptions.length === 0}
        >
          {effortOptions.length === 0 ? (
            <option value="">—</option>
          ) : (
            <>
              <option value="">{effortPlaceholder}</option>
              {effortOptions.map((e) => (
                <option key={e} value={e}>
                  {e}
                  {selectedModel?.default_effort === e ? " (default)" : ""}
                </option>
              ))}
            </>
          )}
        </select>
      </PickerField>
    </>
  );
}

function providerLabel(cli: string): string {
  return cli ? cli.charAt(0).toUpperCase() + cli.slice(1) : cli;
}

function PickerField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-caption text-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
